import { PUBLIC_SITE_ORIGIN, VOLUNTEER_LANDING_URL } from "@/lib/share-channels";

/**
 * Short link codes behind every QR code. Scans hit `/r/<code>` on our own
 * domain so we can count them, then get redirected to the real destination.
 * Codes are stateless: the code itself says where to go.
 *
 *   v-<volunteerId>  -> volunteer landing page, credited to that volunteer
 *   e-<event slug>   -> public event page
 *   general          -> volunteer landing page, no attribution
 */
export type QrTarget =
  | { kind: "volunteer"; volunteerId: string }
  | { kind: "event"; slug: string }
  | { kind: "general" };

export function qrCodeFor(target: QrTarget) {
  if (target.kind === "volunteer") return `v-${target.volunteerId}`;
  if (target.kind === "event") return `e-${target.slug}`;
  return "general";
}

export function qrShortUrl(code: string, origin = PUBLIC_SITE_ORIGIN) {
  return `${origin}/r/${code}`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,120}$/i;

/** Parses a short code back into a destination. Returns null when unusable. */
export function resolveQrCode(rawCode: string): { target: QrTarget; url: string } | null {
  const code = (rawCode ?? "").trim().slice(0, 160);
  if (!code) return null;

  if (code === "general") {
    return { target: { kind: "general" }, url: `${VOLUNTEER_LANDING_URL}?src=qr` };
  }
  if (code.startsWith("v-")) {
    const volunteerId = code.slice(2);
    if (!UUID_RE.test(volunteerId)) return null;
    return {
      target: { kind: "volunteer", volunteerId },
      url: `${VOLUNTEER_LANDING_URL}?ref=${volunteerId}&src=qr`,
    };
  }
  if (code.startsWith("e-")) {
    const slug = code.slice(2).toLowerCase();
    if (!SLUG_RE.test(slug)) return null;
    return {
      target: { kind: "event", slug },
      url: `${PUBLIC_SITE_ORIGIN}/events/${slug}?src=qr`,
    };
  }
  return null;
}

export const QR_KIND_LABELS: Record<QrTarget["kind"], string> = {
  volunteer: "Volunteer recruiting",
  event: "Event",
  general: "General landing",
};
